import { FC, useMemo } from 'react';

import { Transaction, TransactionStatus } from '@ravago/shared/page-data/models/elements';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Checkbox } from '@ravago/shared/radiance/components/checkbox/components/Checkbox/Checkbox';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Tooltip } from '@ravago/shared/radiance/components/tooltip/components/Tooltip/Tooltip';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { useInvoiceDocument } from '../../../../../hooks/use-invoice-document.hook';
import { isOverDue } from '../../../../../utils/invoice-date.utils';
import { canSelectInvoice } from '../../../../../utils/invoice.utils';
import { InvoiceDueDate } from '../shared/invoice-due-date';

type Props = {
   currency: string;
   invoice: Transaction;
   canPayOnline: boolean;
   selected: boolean;
   onSelect: () => void;
   onDeselect: () => void;
};

export const OpenInvoiceCard: FC<Props> = ({ currency, invoice, canPayOnline, selected, onSelect, onDeselect }) => {
   const { handleDownload, handleView } = useInvoiceDocument();
   const translate = useTranslations();
   const format = useFormatter();

   const isInvoiceOverDue = useMemo(() => (invoice.dueDate ? isOverDue(invoice.dueDate) : false), [invoice.dueDate]);

   const invoicePayload = invoice.documentId
      ? { invoiceId: invoice.id, documentId: invoice.documentId, date: invoice.issueDate, type: invoice.type, eInvoiceId: invoice.eInvoiceId }
      : null;

   return (
      <Box direction="column" gap="md">
         <Divider />
         <Box justify="between" gap="none">
            <Box direction="column" gap="3xs">
               <Box minWidth="50%" items="center" gap="none">
                  {canPayOnline && (
                     <Checkbox
                        checked={selected}
                        disabled={!canSelectInvoice(invoice)}
                        onChange={(isSelected) => (isSelected ? onSelect() : onDeselect())}
                     />
                  )}
                  <Typography variant="body-highlight" color="strong">
                     {format.price({ amount: invoice.openAmount, currency })}
                  </Typography>
               </Box>
               <Box justify="end">
                  {invoice.status === TransactionStatus.PENDING && (
                     <Badge leadingIcon="lock" variant="neutral">
                        {translate('invoices.warning.pending')}
                     </Badge>
                  )}
                  {invoice.status === TransactionStatus.PAID && (
                     <Tooltip text={translate('invoices.warning.paid-and-processing-tooltip')}>
                        <Badge trailingIcon="info-circle" variant="success">
                           {translate('invoices.warning.paid-and-processing')}
                        </Badge>
                     </Tooltip>
                  )}
               </Box>
            </Box>
            <Box minWidth="50%" gap="none" items="center" justify="between">
               {isInvoiceOverDue && <Badge variant="error">{translate('invoices.error.overdue')}</Badge>}
               <Box>
                  {invoicePayload && (
                     <Box gap="3xs">
                        <IconButton icon="download" ariaLabel="Download Invoice" onClick={() => handleDownload(invoicePayload)} variant="on-light" />
                        <IconButton icon="eye" ariaLabel="View Invoice" onClick={() => handleView(invoicePayload)} variant="on-light" />
                     </Box>
                  )}
               </Box>
            </Box>
         </Box>
         <Box gap="none">
            <Box minWidth="50%">
               <Typography color="subtle">{translate('invoices.open-invoices.table.table-header.invoice')}</Typography>
            </Box>
            <Box direction="column" gap="3xs" wordBreak="all">
               <Typography>{invoice.eInvoiceId ?? invoice.documentId ?? invoice.id}</Typography>
               <Typography variant="small" color="subtle">
                  {translate(`invoices.invoice-type.${invoice.type}`)}
               </Typography>
            </Box>
         </Box>
         <Box gap="none">
            <Box minWidth="50%">
               <Typography color="subtle">{translate('invoices.open-invoices.table.table-header.date')}</Typography>
            </Box>
            <Box>
               <Typography>{invoice.issueDate}</Typography>
            </Box>
         </Box>
         <Box gap="none">
            <Box minWidth="50%">
               <Typography color="subtle">{translate('invoices.open-invoices.table.table-header.due-date')}</Typography>
            </Box>
            <Box>{invoice.dueDate && <InvoiceDueDate dueDate={invoice.dueDate} invoiceType={invoice.type} />}</Box>
         </Box>
      </Box>
   );
};
